import React, { useState } from "react";
import Page from "../components/Page";
import {
  // Legacy
  useShifts, addNewShift, deleteShift,
  useShiftLogs, addNewShiftLog, deleteShiftLog,
  useStaffTransactions, addNewStaffTransaction, deleteStaffTransaction,
  useStaffPayrollDetails,
  // Departments & Designations
  useDepartments, createDepartment, deleteDepartment,
  useDesignations, createDesignation, deleteDesignation,
  // Employees
  useEmployees, createEmployee, updateEmployee, deleteEmployee,
  createLoginForEmployee, unlinkLoginForEmployee,
  // Holidays
  useHolidayCalendars, createHolidayCalendar, deleteHolidayCalendar,
  useHolidays, addHoliday, deleteHoliday,
  // Attendance
  useAttendance, manualAttendanceEntry, useAttendanceDashboardStats,
  // Leaves
  useLeaveTypes, createLeaveType, useLeaveApplications, applyLeave, approveRejectLeave,
  // Statutory
  useStatutorySettings, updateStatutorySettings,
  // Overtime
  useOvertimeRecords, createOvertimeRecord, approveRejectOvertime,
  // Advances
  useSalaryAdvances, createSalaryAdvance, disburseSalaryAdvance,
  // Deposits
  useSecurityDeposits, recordSecurityDepositReceipt, refundSecurityDeposit,
  // Holds
  useSalaryHolds, createSalaryHold, releaseSalaryHold,
  // Payroll Periods
  usePayrollPeriods, createPayrollPeriod, calculateMonthlyPayroll,
  usePayrollPeriodEmployees, approvePayrollPeriod, reopenPayrollPeriod,
  // Payments
  useSalaryPayments, recordSalaryPayment,
  // Dashboard & Payslip
  usePayrollDashboardKPIs, useEmployeePayslipData
} from "../controllers/hr.controller";
import { useUsers } from "../controllers/users.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import { CURRENCIES } from "../config/currencies.config";
import {
  IconUsers,
  IconCoin,
  IconCircleCheck,
  IconAlertCircle,
  IconCircleX,
  IconReload,
  IconBriefcase,
  IconClock,
  IconCalendar,
  IconPlus,
  IconTrash,
  IconTrendingUp,
  IconTrendingDown,
  IconReportMoney,
  IconBuilding,
  IconCheck,
  IconX,
  IconLock,
  IconLockOpen,
  IconReceipt,
  IconShieldCheck,
  IconSettings,
  IconPrinter,
  IconSearch,
  IconEdit,
  IconEye,
  IconUserPlus,
  IconUserX,
  IconUserCheck,
  IconCash,
  IconFilter
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import toast from "react-hot-toast";

export default function PayrollPage() {
  const { data: storeSettings, isLoading: isSettingsLoading } = useStoreSettings();
  const { data: rawUsers } = useUsers();

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const defaultStartDate = new Date(currentYear, today.getMonth(), 1).toISOString().split("T")[0];
  const defaultEndDate = new Date(currentYear, today.getMonth() + 1, 0).toISOString().split("T")[0];

  // Active top tab
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isProcessing, setIsProcessing] = useState(false);

  // Filter states
  const [dashboardMonth, setDashboardMonth] = useState(currentMonth);
  const [dashboardYear, setDashboardYear] = useState(currentYear);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeDeptFilter, setEmployeeDeptFilter] = useState("");
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState("");
  const [attendanceDate, setAttendanceDate] = useState(today.toISOString().split("T")[0]);
  const [attendanceDeptFilter, setAttendanceDeptFilter] = useState("");
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  // SWR Hooks
  const { data: dashboardKPIs, mutate: mutateKPIs } = usePayrollDashboardKPIs(dashboardMonth, dashboardYear);
  const { data: employees, mutate: mutateEmployees } = useEmployees({
    search: employeeSearch,
    department_id: employeeDeptFilter,
    status: employeeStatusFilter
  });
  const { data: departments, mutate: mutateDepartments } = useDepartments();
  const { data: designations, mutate: mutateDesignations } = useDesignations();
  const { data: holidayCalendars, mutate: mutateCalendars } = useHolidayCalendars();
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const { data: holidays, mutate: mutateHolidays } = useHolidays(selectedCalendarId, dashboardYear);
  const { data: shifts, mutate: mutateShifts } = useShifts();
  const { data: attendanceLogs, mutate: mutateAttendance } = useAttendance({
    date: attendanceDate,
    department_id: attendanceDeptFilter
  });
  const { data: attendanceStats } = useAttendanceDashboardStats(attendanceDate);
  const { data: leaveTypes, mutate: mutateLeaveTypes } = useLeaveTypes();
  const { data: leaveApplications, mutate: mutateLeaveApps } = useLeaveApplications();
  const { data: statutorySettings, mutate: mutateStatutory } = useStatutorySettings();
  const { data: overtimeRecords, mutate: mutateOvertime } = useOvertimeRecords();
  const { data: salaryAdvances, mutate: mutateAdvances } = useSalaryAdvances();
  const { data: securityDeposits, mutate: mutateDeposits } = useSecurityDeposits();
  const { data: salaryHolds, mutate: mutateHolds } = useSalaryHolds();
  const { data: payrollPeriods, mutate: mutatePeriods } = usePayrollPeriods();
  
  // Set default period if not set
  const activePeriod = payrollPeriods?.find((p) => p.id === selectedPeriodId) || payrollPeriods?.[0];
  const currentPeriodId = activePeriod?.id || null;
  const { data: periodEmployees, mutate: mutatePeriodEmployees } = usePayrollPeriodEmployees(currentPeriodId);
  const { data: salaryPayments, mutate: mutatePayments } = useSalaryPayments({ period_id: currentPeriodId });

  // Legacy SWR Hooks
  const [legacyStartDate, setLegacyStartDate] = useState(defaultStartDate);
  const [legacyEndDate, setLegacyEndDate] = useState(defaultEndDate);
  const { data: legacyStaffPayroll, mutate: mutateLegacyPayroll } = useStaffPayrollDetails(legacyStartDate, legacyEndDate);
  const { data: legacyShiftLogs, mutate: mutateLegacyShiftLogs } = useShiftLogs(legacyStartDate, legacyEndDate);
  const { data: legacyTransactions, mutate: mutateLegacyTransactions } = useStaffTransactions(legacyStartDate, legacyEndDate);

  // Modal / Selection State
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployeeForLogin, setSelectedEmployeeForLogin] = useState(null);
  const [payslipEmployeeId, setPayslipEmployeeId] = useState(null);
  const [payslipPeriodId, setPayslipPeriodId] = useState(null);
  const { data: payslipData } = useEmployeePayslipData(payslipPeriodId, payslipEmployeeId);

  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", address: "",
    department_id: "", designation_id: "", employment_type: "permanent",
    basic_salary: 0, monthly_allowance: 0, payment_method: "bank_transfer",
    bank_name: "", bank_account_no: "", pan_no: "", ssf_number: "",
    device_user_id: "", joining_date: new Date().toISOString().split("T")[0]
  });
  const [loginForm, setLoginForm] = useState({ username: "", password: "", role_id: 2 });
  const [deptForm, setDeptForm] = useState({ name: "", code: "" });
  const [desigForm, setDesigForm] = useState({ name: "", code: "", department_id: "" });
  const [holidayForm, setHolidayForm] = useState({ calendar_id: "", holiday_date: "", title: "", holiday_type: "public", overtime_multiplier: 2.0 });
  const [shiftForm, setShiftForm] = useState({ name: "", start_time: "09:00", end_time: "17:00", shift_type: "regular", crosses_midnight: 0, grace_period_minutes: 15, overtime_eligible: 1 });
  const [manualAttForm, setManualAttForm] = useState({ employee_id: "", attendance_date: today.toISOString().split("T")[0], check_in: "09:00", check_out: "17:00", status: "present", remarks: "" });
  const [leaveForm, setLeaveForm] = useState({ employee_id: "", leave_type_id: "", start_date: today.toISOString().split("T")[0], end_date: today.toISOString().split("T")[0], total_days: 1, reason: "" });
  const [overtimeForm, setOvertimeForm] = useState({ employee_id: "", overtime_date: today.toISOString().split("T")[0], calculated_hours: 2, approved_hours: 2, hourly_rate_multiplier: 1.5, reason: "" });
  const [advanceForm, setAdvanceForm] = useState({ employee_id: "", amount: 5000, monthly_installment: 1000, reason: "", repayment_start_date: today.toISOString().split("T")[0] });
  const [depositForm, setDepositForm] = useState({ employee_id: "", amount: 10000, receipt_date: today.toISOString().split("T")[0], payment_mode: "cash", reference_no: "", notes: "" });
  const [holdForm, setHoldForm] = useState({ employee_id: "", period_id: "", hold_amount: 5000, hold_reason: "dispute", remarks: "" });
  const [periodForm, setPeriodForm] = useState({ name: `${new Date().toLocaleString("default", { month: "long" })} ${currentYear}`, period_month: currentMonth, period_year: currentYear, start_date: defaultStartDate, end_date: defaultEndDate, cutoff_date: defaultEndDate });
  const [paymentForm, setPaymentForm] = useState({ employee_id: "", period_id: "", amount: 0, payment_mode: "bank_transfer", reference_no: "", notes: "" });

  const currencyCode = storeSettings?.currency || "NPR";
  const currencyObj = CURRENCIES.find((c) => c.cc === currencyCode);
  const currencySymbol = currencyObj?.symbol || "रू";

  // Helpers
  const formatMoney = (val) => `${currencySymbol} ${Number(val || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // Handlers
  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    if (!employeeForm.first_name || !employeeForm.last_name) {
      toast.error("Please enter first and last name.");
      return;
    }
    try {
      setIsProcessing(true);
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, employeeForm);
        toast.success("Employee updated successfully!");
      } else {
        await createEmployee(employeeForm);
        toast.success("Employee registered successfully without requiring login account!");
      }
      await mutateEmployees();
      document.getElementById("modal-add-employee").close();
      setEditingEmployee(null);
    } catch (err) {
      toast.error(err || "Failed to save employee");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error("Username and password are required.");
      return;
    }
    try {
      setIsProcessing(true);
      await createLoginForEmployee(selectedEmployeeForLogin.id, loginForm);
      toast.success(`Login credentials created for ${selectedEmployeeForLogin.first_name}!`);
      await mutateEmployees();
      document.getElementById("modal-create-login").close();
      setLoginForm({ username: "", password: "", role_id: 2 });
    } catch (err) {
      toast.error(err || "Failed to create login");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnlinkLogin = async (emp) => {
    if (!window.confirm(`Unlink login for ${emp.first_name} ${emp.last_name}? Staff record and payroll will be preserved.`)) return;
    try {
      setIsProcessing(true);
      await unlinkLoginForEmployee(emp.id);
      toast.success("Login account unlinked successfully!");
      await mutateEmployees();
    } catch (err) {
      toast.error(err || "Failed to unlink login");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      await deleteEmployee(id);
      toast.success("Employee record deleted.");
      await mutateEmployees();
    } catch (err) {
      toast.error(err || "Failed to delete employee");
    }
  };

  const handleCalculatePayroll = async () => {
    if (!currentPeriodId) {
      toast.error("Please select a payroll period first.");
      return;
    }
    try {
      setIsProcessing(true);
      toast.loading("Calculating payroll and freezing attendance snapshot...");
      const res = await calculateMonthlyPayroll(currentPeriodId);
      toast.dismiss();
      toast.success(res.message || "Payroll calculated successfully!");
      await mutatePeriods();
      await mutatePeriodEmployees();
      await mutateKPIs();
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to calculate payroll");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprovePayroll = async () => {
    if (!currentPeriodId) return;
    if (!window.confirm("Approve this payroll period? This will post double-entry journal entries to the General Ledger.")) return;
    try {
      setIsProcessing(true);
      toast.loading("Approving payroll and posting balanced journal...");
      await approvePayrollPeriod(currentPeriodId);
      toast.dismiss();
      toast.success("Payroll approved! Double-entry journal entries posted to General Ledger.");
      await mutatePeriods();
      await mutatePeriodEmployees();
      await mutateKPIs();
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to approve payroll");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReopenPayroll = async () => {
    if (!currentPeriodId) return;
    const reason = window.prompt("Reason for reopening this payroll period (audit requirement):");
    if (!reason) return;
    try {
      setIsProcessing(true);
      toast.loading("Reopening payroll and reversing journal...");
      await reopenPayrollPeriod(currentPeriodId, reason);
      toast.dismiss();
      toast.success("Payroll period reopened and reversal journal posted!");
      await mutatePeriods();
      await mutatePeriodEmployees();
      await mutateKPIs();
    } catch (err) {
      toast.dismiss();
      toast.error(err || "Failed to reopen payroll");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.employee_id || !paymentForm.amount || paymentForm.amount <= 0) {
      toast.error("Please select employee and enter a positive payment amount.");
      return;
    }
    try {
      setIsProcessing(true);
      await recordSalaryPayment({ ...paymentForm, period_id: currentPeriodId });
      toast.success("Salary payment recorded and disbursement journal posted!");
      await mutatePayments();
      await mutatePeriodEmployees();
      await mutateKPIs();
      document.getElementById("modal-record-payment").close();
    } catch (err) {
      toast.error(err || "Failed to record payment");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrintPayslip = (empId, periodId) => {
    setPayslipEmployeeId(empId);
    setPayslipPeriodId(periodId || currentPeriodId);
    document.getElementById("modal-view-payslip").showModal();
  };

  return (
    <Page>
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-restro-text tracking-tight flex items-center gap-2">
            <IconUsers stroke={iconStroke} className="text-restro-green w-8 h-8" />
            Human Resources & Payroll
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Complete multi-tenant HRMS: Staff Master, Hybrid Attendance, Leave Engine, Nepal SSF & Double-Entry Accounting
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === "payroll" && activePeriod && (
            <div className="flex items-center gap-2">
              <span className={`badge badge-sm font-semibold uppercase ${activePeriod.status === "approved" ? "badge-success text-white" : activePeriod.status === "calculated" ? "badge-info text-white" : "badge-ghost"}`}>
                Period: {activePeriod.status}
              </span>
              {activePeriod.status !== "approved" && (
                <button
                  onClick={handleCalculatePayroll}
                  disabled={isProcessing}
                  className="btn btn-sm bg-restro-green hover:bg-restro-green-dark text-white border-none gap-1 shadow-sm"
                >
                  <IconReload stroke={iconStroke} size={16} />
                  Calculate & Freeze
                </button>
              )}
              {activePeriod.status === "calculated" && (
                <button
                  onClick={handleApprovePayroll}
                  disabled={isProcessing}
                  className="btn btn-sm btn-success text-white gap-1 shadow-sm"
                >
                  <IconCheck stroke={iconStroke} size={16} />
                  Approve Batch
                </button>
              )}
              {activePeriod.status === "approved" && (
                <button
                  onClick={handleReopenPayroll}
                  disabled={isProcessing}
                  className="btn btn-sm btn-warning text-white gap-1 shadow-sm"
                >
                  <IconLockOpen stroke={iconStroke} size={16} />
                  Reopen Period
                </button>
              )}
            </div>
          )}

          {activeTab === "employees" && (
            <button
              onClick={() => {
                setEditingEmployee(null);
                setEmployeeForm({
                  first_name: "", last_name: "", email: "", phone: "", address: "",
                  department_id: "", designation_id: "", employment_type: "permanent",
                  basic_salary: 0, monthly_allowance: 0, payment_method: "bank_transfer",
                  bank_name: "", bank_account_no: "", pan_no: "", ssf_number: "",
                  device_user_id: "", joining_date: new Date().toISOString().split("T")[0]
                });
                document.getElementById("modal-add-employee").showModal();
              }}
              className="btn btn-sm bg-restro-green hover:bg-restro-green-dark text-white border-none gap-1 shadow-sm"
            >
              <IconUserPlus stroke={iconStroke} size={16} />
              Register Employee
            </button>
          )}
        </div>
      </div>

      {/* Primary Navigation Tabs */}
      <div className="tabs tabs-boxed mb-6 bg-restro-green/5 border border-restro-border-green p-1.5 flex flex-wrap gap-1 rounded-2xl">
        <button
          onClick={() => setActiveTab("dashboard")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "dashboard" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconReportMoney stroke={iconStroke} size={16} />
          KPIs Dashboard
        </button>

        <button
          onClick={() => setActiveTab("employees")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "employees" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconUsers stroke={iconStroke} size={16} />
          Employee Master ({employees?.length || 0})
        </button>

        <button
          onClick={() => setActiveTab("organization")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "organization" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconBuilding stroke={iconStroke} size={16} />
          Departments & Designations
        </button>

        <button
          onClick={() => setActiveTab("holidays")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "holidays" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconCalendar stroke={iconStroke} size={16} />
          Holidays
        </button>

        <button
          onClick={() => setActiveTab("shifts")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "shifts" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconClock stroke={iconStroke} size={16} />
          Shifts & Rosters
        </button>

        <button
          onClick={() => setActiveTab("attendance")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "attendance" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconUserCheck stroke={iconStroke} size={16} />
          Hybrid Attendance
        </button>

        <button
          onClick={() => setActiveTab("leaves")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "leaves" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconBriefcase stroke={iconStroke} size={16} />
          Leaves
        </button>

        <button
          onClick={() => setActiveTab("overtime")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "overtime" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconTrendingUp stroke={iconStroke} size={16} />
          Overtime Approval
        </button>

        <button
          onClick={() => setActiveTab("advances")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "advances" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconCoin stroke={iconStroke} size={16} />
          Advances & Loans
        </button>

        <button
          onClick={() => setActiveTab("deposits")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "deposits" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconShieldCheck stroke={iconStroke} size={16} />
          Joining Deposits
        </button>

        <button
          onClick={() => setActiveTab("holds")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "holds" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconLock stroke={iconStroke} size={16} />
          Salary Holds
        </button>

        <button
          onClick={() => setActiveTab("payroll")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "payroll" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconReceipt stroke={iconStroke} size={16} />
          Monthly Payroll Engine
        </button>

        <button
          onClick={() => setActiveTab("payments")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "payments" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconCash stroke={iconStroke} size={16} />
          Disbursements
        </button>

        <button
          onClick={() => setActiveTab("statutory")}
          className={`tab tab-md rounded-xl transition-all gap-1.5 ${activeTab === "statutory" ? "bg-restro-green text-white font-bold shadow-sm" : "text-gray-400 hover:text-restro-text"}`}
        >
          <IconSettings stroke={iconStroke} size={16} />
          Nepal SSF Settings
        </button>
      </div>

      {/* ======================================================== */}
      {/* TAB 1: DASHBOARD & REAL-TIME RECONCILIATION KPIS        */}
      {/* ======================================================== */}
      {activeTab === "dashboard" && (
        <div className="space-y-6">
          {/* Filter Bar */}
          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-gray-500">Period:</span>
              <select
                value={dashboardMonth}
                onChange={(e) => setDashboardMonth(Number(e.target.value))}
                className="select select-sm select-bordered"
              >
                {[
                  "January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"
                ].map((m, idx) => (
                  <option key={idx} value={idx + 1}>{m}</option>
                ))}
              </select>
              <input
                type="number"
                value={dashboardYear}
                onChange={(e) => setDashboardYear(Number(e.target.value))}
                className="input input-sm input-bordered w-24"
              />
            </div>
            <div className="text-xs text-gray-400">
              * Reconciliation engine actively verifies Net Payable vs Paid & Liabilities
            </div>
          </div>

          {/* KPI Dashboard Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card 1: Gross Salary */}
            <div className="p-5 rounded-2xl bg-base-100 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Gross Salary</p>
                <h3 className="text-2xl font-black text-restro-text mt-1">{formatMoney(dashboardKPIs?.gross_salary)}</h3>
                <p className="text-xs text-gray-400 mt-1">Base + Allowances + OT</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <IconCoin stroke={iconStroke} size={24} />
              </div>
            </div>

            {/* Card 2: Total Deductions */}
            <div className="p-5 rounded-2xl bg-base-100 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Deductions</p>
                <h3 className="text-2xl font-black text-rose-600 mt-1">{formatMoney(dashboardKPIs?.total_deductions)}</h3>
                <p className="text-xs text-gray-400 mt-1">Absence + SSF + Tax</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
                <IconTrendingDown stroke={iconStroke} size={24} />
              </div>
            </div>

            {/* Card 3: Net Due Now */}
            <div className="p-5 rounded-2xl bg-base-100 border border-emerald-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Net Due Now</p>
                <h3 className="text-2xl font-black text-emerald-700 mt-1">{formatMoney(dashboardKPIs?.net_due_now)}</h3>
                <p className="text-xs text-gray-400 mt-1">After Advances & Holds</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <IconCheck stroke={iconStroke} size={24} />
              </div>
            </div>

            {/* Card 4: Total Employer Cost */}
            <div className="p-5 rounded-2xl bg-base-100 border border-indigo-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-wider">Total Employer Cost</p>
                <h3 className="text-2xl font-black text-indigo-700 mt-1">{formatMoney(dashboardKPIs?.total_employer_cost)}</h3>
                <p className="text-xs text-gray-400 mt-1">Gross + 20% Employer SSF</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <IconBriefcase stroke={iconStroke} size={24} />
              </div>
            </div>
          </div>

          {/* Secondary KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="p-4 rounded-xl bg-base-100 border border-gray-100">
              <span className="text-[11px] font-bold text-gray-400 uppercase">Already Paid</span>
              <p className="text-lg font-bold text-emerald-600 mt-0.5">{formatMoney(dashboardKPIs?.already_paid)}</p>
            </div>
            <div className="p-4 rounded-xl bg-base-100 border border-gray-100">
              <span className="text-[11px] font-bold text-gray-400 uppercase">Still Unpaid</span>
              <p className="text-lg font-bold text-amber-600 mt-0.5">{formatMoney(dashboardKPIs?.still_unpaid)}</p>
            </div>
            <div className="p-4 rounded-xl bg-base-100 border border-gray-100">
              <span className="text-[11px] font-bold text-gray-400 uppercase">Salary Holds</span>
              <p className="text-lg font-bold text-purple-600 mt-0.5">{formatMoney(dashboardKPIs?.salary_holds)}</p>
            </div>
            <div className="p-4 rounded-xl bg-base-100 border border-gray-100">
              <span className="text-[11px] font-bold text-gray-400 uppercase">Advance Recoveries</span>
              <p className="text-lg font-bold text-blue-600 mt-0.5">{formatMoney(dashboardKPIs?.advance_recoveries)}</p>
            </div>
            <div className="p-4 rounded-xl bg-base-100 border border-gray-100">
              <span className="text-[11px] font-bold text-gray-400 uppercase">SSF Liabilities</span>
              <p className="text-lg font-bold text-teal-600 mt-0.5">{formatMoney(dashboardKPIs?.ssf_liabilities)}</p>
            </div>
            <div className="p-4 rounded-xl bg-base-100 border border-gray-100">
              <span className="text-[11px] font-bold text-gray-400 uppercase">Active Staff</span>
              <p className="text-lg font-bold text-restro-text mt-0.5">{dashboardKPIs?.active_employees || 0} Staff</p>
            </div>
          </div>

          {/* Quick Shortcuts */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              onClick={() => setActiveTab("employees")}
              className="p-5 rounded-2xl bg-base-100 border border-gray-100 hover:border-restro-green cursor-pointer transition-all shadow-sm"
            >
              <h4 className="font-bold text-restro-text flex items-center gap-2">
                <IconUsers stroke={iconStroke} size={18} className="text-restro-green" />
                Staff Directory
              </h4>
              <p className="text-xs text-gray-400 mt-1">Manage employee master records, banking info, and optional login credentials.</p>
            </div>

            <div
              onClick={() => setActiveTab("attendance")}
              className="p-5 rounded-2xl bg-base-100 border border-gray-100 hover:border-restro-green cursor-pointer transition-all shadow-sm"
            >
              <h4 className="font-bold text-restro-text flex items-center gap-2">
                <IconUserCheck stroke={iconStroke} size={18} className="text-restro-green" />
                Hybrid Attendance
              </h4>
              <p className="text-xs text-gray-400 mt-1">Review live biometric punches from ZKTeco ADMS and manual attendance adjustments.</p>
            </div>

            <div
              onClick={() => setActiveTab("payroll")}
              className="p-5 rounded-2xl bg-base-100 border border-gray-100 hover:border-restro-green cursor-pointer transition-all shadow-sm"
            >
              <h4 className="font-bold text-restro-text flex items-center gap-2">
                <IconReceipt stroke={iconStroke} size={18} className="text-restro-green" />
                Process Payroll
              </h4>
              <p className="text-xs text-gray-400 mt-1">Calculate monthly salaries, freeze snapshots, and post balanced General Ledger journals.</p>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 2: EMPLOYEE MASTER (DECOUPLED FROM LOGIN ACCOUNTS)   */}
      {/* ======================================================== */}
      {activeTab === "employees" && (
        <div className="space-y-4">
          {/* Controls Bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <div className="relative w-full md:w-64">
                <input
                  type="text"
                  placeholder="Search staff by name, code, phone..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="input input-sm input-bordered w-full pl-8"
                />
                <IconSearch stroke={iconStroke} size={16} className="absolute left-2.5 top-2 text-gray-400" />
              </div>

              <select
                value={employeeDeptFilter}
                onChange={(e) => setEmployeeDeptFilter(e.target.value)}
                className="select select-sm select-bordered"
              >
                <option value="">All Departments</option>
                {departments?.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>

              <select
                value={employeeStatusFilter}
                onChange={(e) => setEmployeeStatusFilter(e.target.value)}
                className="select select-sm select-bordered"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="probation">Probation</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>

            <span className="text-xs text-gray-400">
              Showing {employees?.length || 0} staff members
            </span>
          </div>

          {/* Employee Master Table */}
          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Department & Role</th>
                  <th>Employment</th>
                  <th>Salary Structure</th>
                  <th>Biometric PIN</th>
                  <th>System Login</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-400">
                      No employees found matching filter.
                    </td>
                  </tr>
                ) : (
                  employees?.map((emp) => (
                    <tr key={emp.id} className="hover:bg-gray-50/50">
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-restro-green/10 text-restro-green font-bold flex items-center justify-center text-sm">
                            {emp.first_name?.[0]}{emp.last_name?.[0]}
                          </div>
                          <div>
                            <p className="font-bold text-restro-text">{emp.first_name} {emp.last_name}</p>
                            <p className="text-xs text-gray-400 font-mono">{emp.employee_code || `EMP-${emp.id}`}</p>
                          </div>
                        </div>
                      </td>

                      <td>
                        <p className="font-semibold text-xs text-restro-text">{emp.department_name || "Unassigned"}</p>
                        <p className="text-xs text-gray-400">{emp.designation_name || "Staff"}</p>
                      </td>

                      <td>
                        <span className={`badge badge-xs uppercase font-bold text-[10px] ${emp.status === "active" ? "badge-success text-white" : emp.status === "probation" ? "badge-warning text-white" : "badge-ghost"}`}>
                          {emp.employment_type || emp.status}
                        </span>
                      </td>

                      <td>
                        <p className="font-mono text-xs font-bold text-restro-text">{formatMoney(emp.basic_salary)}</p>
                        <p className="text-[11px] text-gray-400">+ {formatMoney(emp.monthly_allowance)} allow</p>
                      </td>

                      <td>
                        {emp.device_user_id ? (
                          <span className="badge badge-sm badge-outline font-mono text-xs">PIN: {emp.device_user_id}</span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">No PIN</span>
                        )}
                      </td>

                      <td>
                        {emp.linked_user_id ? (
                          <div className="flex items-center gap-1.5">
                            <span className="badge badge-xs badge-success text-white">Linked</span>
                            <span className="text-xs text-gray-500 font-mono">@{emp.username}</span>
                          </div>
                        ) : (
                          <span className="badge badge-xs badge-ghost text-gray-400">No Login</span>
                        )}
                      </td>

                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {emp.linked_user_id ? (
                            <button
                              onClick={() => handleUnlinkLogin(emp)}
                              title="Unlink Login Account"
                              className="btn btn-ghost btn-xs text-rose-500 hover:bg-rose-50"
                            >
                              <IconUserX stroke={iconStroke} size={15} />
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedEmployeeForLogin(emp);
                                setLoginForm({ username: `${emp.first_name.toLowerCase()}.${emp.last_name.toLowerCase()}`, password: "", role_id: 2 });
                                document.getElementById("modal-create-login").showModal();
                              }}
                              title="Create Login Account"
                              className="btn btn-ghost btn-xs text-restro-green hover:bg-restro-green/10"
                            >
                              <IconUserPlus stroke={iconStroke} size={15} />
                            </button>
                          )}

                          <button
                            onClick={() => {
                              setEditingEmployee(emp);
                              setEmployeeForm({
                                first_name: emp.first_name || "",
                                last_name: emp.last_name || "",
                                email: emp.email || "",
                                phone: emp.phone || "",
                                address: emp.address || "",
                                department_id: emp.department_id || "",
                                designation_id: emp.designation_id || "",
                                employment_type: emp.employment_type || "permanent",
                                basic_salary: emp.basic_salary || 0,
                                monthly_allowance: emp.monthly_allowance || 0,
                                payment_method: emp.payment_method || "bank_transfer",
                                bank_name: emp.bank_name || "",
                                bank_account_no: emp.bank_account_no || "",
                                pan_no: emp.pan_no || "",
                                ssf_number: emp.ssf_number || "",
                                device_user_id: emp.device_user_id || "",
                                joining_date: emp.joining_date ? emp.joining_date.split("T")[0] : ""
                              });
                              document.getElementById("modal-add-employee").showModal();
                            }}
                            className="btn btn-ghost btn-xs text-gray-500"
                            title="Edit Employee"
                          >
                            <IconEdit stroke={iconStroke} size={15} />
                          </button>

                          <button
                            onClick={() => handleDeleteEmployee(emp.id)}
                            className="btn btn-ghost btn-xs text-rose-500 hover:bg-rose-50"
                            title="Delete Employee"
                          >
                            <IconTrash stroke={iconStroke} size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 3: DEPARTMENTS & DESIGNATIONS                       */}
      {/* ======================================================== */}
      {activeTab === "organization" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Departments */}
          <div className="bg-base-100 p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-restro-text text-lg">Departments</h3>
                <p className="text-xs text-gray-400">Organizational units & cost centers</p>
              </div>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!deptForm.name) return;
                try {
                  await createDepartment(deptForm.name, deptForm.code);
                  toast.success("Department created!");
                  setDeptForm({ name: "", code: "" });
                  await mutateDepartments();
                } catch (err) {
                  toast.error(err || "Failed to create department");
                }
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                placeholder="Department Name (e.g. Kitchen, Front Desk)"
                value={deptForm.name}
                onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                className="input input-sm input-bordered flex-1"
                required
              />
              <input
                type="text"
                placeholder="Code"
                value={deptForm.code}
                onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value })}
                className="input input-sm input-bordered w-20"
              />
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Add
              </button>
            </form>

            <div className="divide-y divide-gray-100">
              {departments?.map((d) => (
                <div key={d.id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-sm text-restro-text">{d.name}</span>
                    {d.code && <span className="ml-2 text-xs font-mono text-gray-400">[{d.code}]</span>}
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete department ${d.name}?`)) return;
                      try {
                        await deleteDepartment(d.id);
                        toast.success("Department deleted");
                        await mutateDepartments();
                      } catch (err) {
                        toast.error(err || "Failed to delete department");
                      }
                    }}
                    className="btn btn-ghost btn-xs text-rose-500"
                  >
                    <IconTrash stroke={iconStroke} size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Designations */}
          <div className="bg-base-100 p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-restro-text text-lg">Designations & Job Titles</h3>
                <p className="text-xs text-gray-400">Positions and job levels</p>
              </div>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!desigForm.name) return;
                try {
                  await createDesignation(desigForm.name, desigForm.code, desigForm.department_id || null);
                  toast.success("Designation created!");
                  setDesigForm({ name: "", code: "", department_id: "" });
                  await mutateDesignations();
                } catch (err) {
                  toast.error(err || "Failed to create designation");
                }
              }}
              className="flex items-center gap-2"
            >
              <input
                type="text"
                placeholder="Title (e.g. Head Chef, Manager)"
                value={desigForm.name}
                onChange={(e) => setDesigForm({ ...desigForm, name: e.target.value })}
                className="input input-sm input-bordered flex-1"
                required
              />
              <select
                value={desigForm.department_id}
                onChange={(e) => setDesigForm({ ...desigForm, department_id: e.target.value })}
                className="select select-sm select-bordered w-32"
              >
                <option value="">Dept</option>
                {departments?.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Add
              </button>
            </form>

            <div className="divide-y divide-gray-100">
              {designations?.map((dg) => (
                <div key={dg.id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-sm text-restro-text">{dg.name}</span>
                    {dg.department_name && <span className="ml-2 text-xs text-gray-400">({dg.department_name})</span>}
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete designation ${dg.name}?`)) return;
                      try {
                        await deleteDesignation(dg.id);
                        toast.success("Designation deleted");
                        await mutateDesignations();
                      } catch (err) {
                        toast.error(err || "Failed to delete designation");
                      }
                    }}
                    className="btn btn-ghost btn-xs text-rose-500"
                  >
                    <IconTrash stroke={iconStroke} size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 4: HOLIDAYS & WORK CALENDARS                        */}
      {/* ======================================================== */}
      {activeTab === "holidays" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-gray-500">Year:</span>
              <input
                type="number"
                value={dashboardYear}
                onChange={(e) => setDashboardYear(Number(e.target.value))}
                className="input input-sm input-bordered w-24"
              />
            </div>

            <button
              onClick={() => {
                setHolidayForm({
                  calendar_id: holidayCalendars?.[0]?.id || "",
                  holiday_date: today.toISOString().split("T")[0],
                  title: "",
                  holiday_type: "public",
                  overtime_multiplier: 2.0
                });
                document.getElementById("modal-add-holiday").showModal();
              }}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Add Holiday
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Date</th>
                  <th>Holiday Title</th>
                  <th>Type</th>
                  <th>Holiday Overtime Rate</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {holidays?.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-8 text-gray-400">
                      No holidays configured for {dashboardYear}.
                    </td>
                  </tr>
                ) : (
                  holidays?.map((h) => (
                    <tr key={h.id} className="hover:bg-gray-50/50">
                      <td className="font-mono text-xs font-bold text-restro-text">{h.holiday_date}</td>
                      <td className="font-semibold text-sm text-restro-text">{h.title}</td>
                      <td>
                        <span className="badge badge-xs uppercase font-bold badge-info text-white">{h.holiday_type}</span>
                      </td>
                      <td className="font-mono text-xs font-bold text-emerald-600">{h.overtime_multiplier || 2.0}x Hourly Pay</td>
                      <td className="text-right">
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete holiday ${h.title}?`)) return;
                            try {
                              await deleteHoliday(h.id);
                              toast.success("Holiday deleted");
                              await mutateHolidays();
                            } catch (err) {
                              toast.error(err || "Failed to delete holiday");
                            }
                          }}
                          className="btn btn-ghost btn-xs text-rose-500"
                        >
                          <IconTrash stroke={iconStroke} size={15} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 5: SHIFTS & ROSTERS                                  */}
      {/* ======================================================== */}
      {activeTab === "shifts" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="font-bold text-restro-text">Configured Shifts</h3>
              <p className="text-xs text-gray-400">Fixed, rotational, and night shifts crossing midnight</p>
            </div>
            <button
              onClick={() => document.getElementById("modal-add-shift-upgraded").showModal()}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Add Shift
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Shift Name</th>
                  <th>Hours</th>
                  <th>Type</th>
                  <th>Crosses Midnight</th>
                  <th>Grace Period</th>
                  <th>OT Eligible</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shifts?.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50/50">
                    <td className="font-bold text-restro-text">{s.name}</td>
                    <td className="font-mono text-xs">{s.start_time} - {s.end_time}</td>
                    <td>
                      <span className="badge badge-xs uppercase font-bold badge-ghost">{s.shift_type || "regular"}</span>
                    </td>
                    <td>
                      {s.crosses_midnight ? (
                        <span className="badge badge-xs badge-warning text-white">Yes (Night Shift)</span>
                      ) : (
                        <span className="text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="text-xs">{s.grace_period_minutes || 15} mins</td>
                    <td>
                      {s.overtime_eligible !== 0 ? (
                        <span className="badge badge-xs badge-success text-white">Eligible</span>
                      ) : (
                        <span className="text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Delete shift ${s.name}?`)) return;
                          try {
                            await deleteShift(s.id);
                            toast.success("Shift deleted");
                            await mutateShifts();
                          } catch (err) {
                            toast.error(err || "Failed to delete shift");
                          }
                        }}
                        className="btn btn-ghost btn-xs text-rose-500"
                      >
                        <IconTrash stroke={iconStroke} size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 6: HYBRID ATTENDANCE (BIOMETRIC + MANUAL AUDITED)    */}
      {/* ======================================================== */}
      {activeTab === "attendance" && (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-gray-500">Date:</span>
              <input
                type="date"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
                className="input input-sm input-bordered"
              />
              <select
                value={attendanceDeptFilter}
                onChange={(e) => setAttendanceDeptFilter(e.target.value)}
                className="select select-sm select-bordered"
              >
                <option value="">All Departments</option>
                {departments?.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <button
              onClick={() => document.getElementById("modal-manual-attendance").showModal()}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconEdit stroke={iconStroke} size={16} />
              Manual Correction / Entry
            </button>
          </div>

          {/* Daily Attendance Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-3 bg-base-100 rounded-xl border border-gray-100">
              <p className="text-[11px] font-bold text-gray-400 uppercase">Present</p>
              <p className="text-xl font-black text-emerald-600">{attendanceStats?.present_count || 0}</p>
            </div>
            <div className="p-3 bg-base-100 rounded-xl border border-gray-100">
              <p className="text-[11px] font-bold text-gray-400 uppercase">Late</p>
              <p className="text-xl font-black text-amber-500">{attendanceStats?.late_count || 0}</p>
            </div>
            <div className="p-3 bg-base-100 rounded-xl border border-gray-100">
              <p className="text-[11px] font-bold text-gray-400 uppercase">Absent</p>
              <p className="text-xl font-black text-rose-500">{attendanceStats?.absent_count || 0}</p>
            </div>
            <div className="p-3 bg-base-100 rounded-xl border border-gray-100">
              <p className="text-[11px] font-bold text-gray-400 uppercase">On Leave</p>
              <p className="text-xl font-black text-purple-600">{attendanceStats?.leave_count || 0}</p>
            </div>
            <div className="p-3 bg-base-100 rounded-xl border border-gray-100">
              <p className="text-[11px] font-bold text-gray-400 uppercase">OT Hours</p>
              <p className="text-xl font-black text-blue-600">{attendanceStats?.total_overtime_hours || 0} hrs</p>
            </div>
          </div>

          {/* Attendance Log Table */}
          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Shift</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Working Hours</th>
                  <th>Late Mark</th>
                  <th>Status</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {attendanceLogs?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-400">
                      No attendance entries recorded for {attendanceDate}.
                    </td>
                  </tr>
                ) : (
                  attendanceLogs?.map((att) => (
                    <tr key={att.id} className="hover:bg-gray-50/50">
                      <td>
                        <p className="font-bold text-restro-text">{att.first_name} {att.last_name}</p>
                        <p className="text-xs text-gray-400">{att.department_name}</p>
                      </td>
                      <td className="text-xs">{att.shift_name || "Standard"}</td>
                      <td className="font-mono text-xs">{att.check_in_time || "--:--"}</td>
                      <td className="font-mono text-xs">{att.check_out_time || "--:--"}</td>
                      <td className="font-mono text-xs font-bold">{att.working_hours ? `${att.working_hours} hrs` : "-"}</td>
                      <td>
                        {att.is_late ? (
                          <span className="badge badge-xs badge-warning text-white">Late ({att.late_minutes}m)</span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-xs uppercase font-bold ${att.status === "present" ? "badge-success text-white" : att.status === "absent" ? "badge-error text-white" : att.status === "half_day" ? "badge-warning text-white" : "badge-info text-white"}`}>
                          {att.status}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-xs badge-outline text-[10px] uppercase">
                          {att.source || "biometric"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 7: LEAVE MANAGEMENT                                  */}
      {/* ======================================================== */}
      {activeTab === "leaves" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="font-bold text-restro-text">Leave Applications & Approvals</h3>
              <p className="text-xs text-gray-400">Approved leaves automatically prevent unexcused absence deductions</p>
            </div>
            <button
              onClick={() => document.getElementById("modal-apply-leave").showModal()}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Apply Leave
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Leave Type</th>
                  <th>Duration</th>
                  <th>Days</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {leaveApplications?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">
                      No leave applications found.
                    </td>
                  </tr>
                ) : (
                  leaveApplications?.map((la) => (
                    <tr key={la.id} className="hover:bg-gray-50/50">
                      <td>
                        <p className="font-bold text-restro-text">{la.first_name} {la.last_name}</p>
                        <p className="text-xs text-gray-400">{la.department_name}</p>
                      </td>
                      <td className="font-semibold text-xs text-restro-text">{la.leave_type_name}</td>
                      <td className="font-mono text-xs">{la.start_date} to {la.end_date}</td>
                      <td className="font-bold text-xs">{la.total_days} days</td>
                      <td className="text-xs text-gray-500 max-w-xs truncate">{la.reason || "-"}</td>
                      <td>
                        <span className={`badge badge-xs uppercase font-bold ${la.status === "approved" ? "badge-success text-white" : la.status === "rejected" ? "badge-error text-white" : "badge-warning text-white"}`}>
                          {la.status}
                        </span>
                      </td>
                      <td className="text-right">
                        {la.status === "pending" && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={async () => {
                                try {
                                  await approveRejectLeave(la.id, "approved", "Approved by HR");
                                  toast.success("Leave approved!");
                                  await mutateLeaveApps();
                                } catch (err) {
                                  toast.error(err || "Failed to approve leave");
                                }
                              }}
                              className="btn btn-xs btn-success text-white"
                            >
                              Approve
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await approveRejectLeave(la.id, "rejected", "Rejected by HR");
                                  toast.success("Leave rejected");
                                  await mutateLeaveApps();
                                } catch (err) {
                                  toast.error(err || "Failed to reject leave");
                                }
                              }}
                              className="btn btn-xs btn-error text-white"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 8: OVERTIME APPROVAL                                */}
      {/* ======================================================== */}
      {activeTab === "overtime" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="font-bold text-restro-text">Overtime Records & Multipliers</h3>
              <p className="text-xs text-gray-400">Review calculated extra hours; approved OT flows directly into payroll calculation</p>
            </div>
            <button
              onClick={() => document.getElementById("modal-add-overtime").showModal()}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Record Overtime
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Date</th>
                  <th>Calculated Hours</th>
                  <th>Approved Hours</th>
                  <th>Rate Multiplier</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {overtimeRecords?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">
                      No overtime records pending review.
                    </td>
                  </tr>
                ) : (
                  overtimeRecords?.map((ot) => (
                    <tr key={ot.id} className="hover:bg-gray-50/50">
                      <td>
                        <p className="font-bold text-restro-text">{ot.first_name} {ot.last_name}</p>
                        <p className="text-xs text-gray-400">{ot.department_name}</p>
                      </td>
                      <td className="font-mono text-xs">{ot.overtime_date}</td>
                      <td className="font-bold text-xs">{ot.calculated_hours} hrs</td>
                      <td className="font-mono text-xs font-bold text-emerald-600">{ot.approved_hours || ot.calculated_hours} hrs</td>
                      <td className="font-mono text-xs font-bold text-indigo-600">{ot.hourly_rate_multiplier || 1.5}x</td>
                      <td>
                        <span className={`badge badge-xs uppercase font-bold ${ot.status === "approved" ? "badge-success text-white" : ot.status === "rejected" ? "badge-error text-white" : "badge-warning text-white"}`}>
                          {ot.status}
                        </span>
                      </td>
                      <td className="text-right">
                        {ot.status === "pending" && (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={async () => {
                                try {
                                  await approveRejectOvertime(ot.id, "approved", ot.calculated_hours, "Approved by Manager");
                                  toast.success("Overtime approved!");
                                  await mutateOvertime();
                                } catch (err) {
                                  toast.error(err || "Failed to approve overtime");
                                }
                              }}
                              className="btn btn-xs btn-success text-white"
                            >
                              Approve
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await approveRejectOvertime(ot.id, "rejected", 0, "Rejected by Manager");
                                  toast.success("Overtime rejected");
                                  await mutateOvertime();
                                } catch (err) {
                                  toast.error(err || "Failed to reject overtime");
                                }
                              }}
                              className="btn btn-xs btn-error text-white"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 9: SALARY ADVANCES & LOANS                          */}
      {/* ======================================================== */}
      {activeTab === "advances" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="font-bold text-restro-text">Salary Advances & Loan Recoveries</h3>
              <p className="text-xs text-gray-400">Disbursed advances are tracked as receivables and recovered monthly via payroll deductions</p>
            </div>
            <button
              onClick={() => document.getElementById("modal-add-advance-upgraded").showModal()}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Issue Advance
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Advance Amount</th>
                  <th>Monthly Recovery</th>
                  <th>Remaining Balance</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {salaryAdvances?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-400">
                      No active salary advances recorded.
                    </td>
                  </tr>
                ) : (
                  salaryAdvances?.map((adv) => (
                    <tr key={adv.id} className="hover:bg-gray-50/50">
                      <td>
                        <p className="font-bold text-restro-text">{adv.first_name} {adv.last_name}</p>
                        <p className="text-xs text-gray-400">{adv.department_name}</p>
                      </td>
                      <td className="font-mono text-xs font-bold text-restro-text">{formatMoney(adv.amount)}</td>
                      <td className="font-mono text-xs">{formatMoney(adv.monthly_installment)} / mo</td>
                      <td className="font-mono text-xs font-bold text-rose-600">{formatMoney(adv.remaining_balance)}</td>
                      <td>
                        <span className={`badge badge-xs uppercase font-bold ${adv.status === "disbursed" ? "badge-info text-white" : adv.status === "completed" ? "badge-success text-white" : "badge-warning text-white"}`}>
                          {adv.status}
                        </span>
                      </td>
                      <td className="text-right">
                        {adv.status === "approved" && (
                          <button
                            onClick={async () => {
                              try {
                                await disburseSalaryAdvance(adv.id, "cash", `ADV-${adv.id}`);
                                toast.success("Advance disbursed and receivable journal posted!");
                                await mutateAdvances();
                              } catch (err) {
                                toast.error(err || "Failed to disburse advance");
                              }
                            }}
                            className="btn btn-xs btn-success text-white"
                          >
                            Disburse Cash
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 10: JOINING DEPOSITS (STRICT LIABILITY REGISTER)     */}
      {/* ======================================================== */}
      {activeTab === "deposits" && (
        <div className="space-y-4">
          <div className="alert alert-info bg-blue-50/80 border-blue-200 text-blue-900 rounded-2xl flex items-start gap-3">
            <IconShieldCheck stroke={iconStroke} size={24} className="text-blue-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-bold text-sm">Strict Balance Sheet Liability Accounting</p>
              Joining / Security Deposits collected from staff are strictly treated as a company liability, never recorded as salary income, expense, or revenue. Receipts post <span className="font-mono font-bold">Dr. Cash/Bank, Cr. Employee Deposit Liability</span>.
            </div>
          </div>

          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="font-bold text-restro-text">Security Deposits Register</h3>
              <p className="text-xs text-gray-400">Track refundable deposits held and repayment workflows</p>
            </div>
            <button
              onClick={() => document.getElementById("modal-record-deposit").showModal()}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Receive Deposit
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Deposit Amount</th>
                  <th>Receipt Date</th>
                  <th>Status</th>
                  <th>Refund Date</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {securityDeposits?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-400">
                      No security deposits recorded.
                    </td>
                  </tr>
                ) : (
                  securityDeposits?.map((dep) => (
                    <tr key={dep.id} className="hover:bg-gray-50/50">
                      <td>
                        <p className="font-bold text-restro-text">{dep.first_name} {dep.last_name}</p>
                        <p className="text-xs text-gray-400">{dep.department_name}</p>
                      </td>
                      <td className="font-mono text-xs font-bold text-indigo-700">{formatMoney(dep.amount)}</td>
                      <td className="font-mono text-xs">{dep.receipt_date}</td>
                      <td>
                        <span className={`badge badge-xs uppercase font-bold ${dep.status === "held" ? "badge-info text-white" : "badge-success text-white"}`}>
                          {dep.status === "held" ? "Liability Held" : "Refunded"}
                        </span>
                      </td>
                      <td className="text-xs text-gray-400">{dep.refund_date || "-"}</td>
                      <td className="text-right">
                        {dep.status === "held" && (
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Refund deposit of ${formatMoney(dep.amount)} to ${dep.first_name}?`)) return;
                              try {
                                await refundSecurityDeposit(dep.id, "cash", `REF-${dep.id}`, "Refund on exit/confirmation");
                                toast.success("Deposit refunded and liability cleared!");
                                await mutateDeposits();
                              } catch (err) {
                                toast.error(err || "Failed to refund deposit");
                              }
                            }}
                            className="btn btn-xs btn-outline btn-success"
                          >
                            Refund Liability
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 11: SALARY HOLDS REGISTER                           */}
      {/* ======================================================== */}
      {activeTab === "holds" && (
        <div className="space-y-4">
          <div className="alert alert-warning bg-amber-50/80 border-amber-200 text-amber-900 rounded-2xl flex items-start gap-3">
            <IconLock stroke={iconStroke} size={24} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-bold text-sm">Independent Hold Liability Register</p>
              Salary Holds withhold net salary temporarily due to notice periods, disputes, or asset clearances. They remain outstanding liabilities until explicitly released.
            </div>
          </div>

          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="font-bold text-restro-text">Salary Holds Register</h3>
              <p className="text-xs text-gray-400">Held amounts are deducted from immediate payout and released on clearance</p>
            </div>
            <button
              onClick={() => document.getElementById("modal-record-hold").showModal()}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Record Hold
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Hold Amount</th>
                  <th>Hold Reason</th>
                  <th>Status</th>
                  <th>Release Date</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {salaryHolds?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-400">
                      No salary holds currently active.
                    </td>
                  </tr>
                ) : (
                  salaryHolds?.map((sh) => (
                    <tr key={sh.id} className="hover:bg-gray-50/50">
                      <td>
                        <p className="font-bold text-restro-text">{sh.first_name} {sh.last_name}</p>
                        <p className="text-xs text-gray-400">{sh.department_name}</p>
                      </td>
                      <td className="font-mono text-xs font-bold text-purple-700">{formatMoney(sh.hold_amount)}</td>
                      <td className="text-xs font-semibold capitalize">{sh.hold_reason?.replace(/_/g, " ")}</td>
                      <td>
                        <span className={`badge badge-xs uppercase font-bold ${sh.status === "held" ? "badge-warning text-white" : "badge-success text-white"}`}>
                          {sh.status}
                        </span>
                      </td>
                      <td className="text-xs text-gray-400">{sh.release_date || "-"}</td>
                      <td className="text-right">
                        {sh.status === "held" && (
                          <button
                            onClick={async () => {
                              const reason = window.prompt("Release justification remarks:");
                              if (!reason) return;
                              try {
                                await releaseSalaryHold(sh.id, reason);
                                toast.success("Salary hold released!");
                                await mutateHolds();
                              } catch (err) {
                                toast.error(err || "Failed to release hold");
                              }
                            }}
                            className="btn btn-xs btn-outline btn-success"
                          >
                            Release Hold
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 12: MONTHLY PAYROLL PERIODS & CALCULATION ENGINE     */}
      {/* ======================================================== */}
      {activeTab === "payroll" && (
        <div className="space-y-4">
          {/* Period Selector Bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <span className="text-sm font-bold text-gray-500">Payroll Period:</span>
              <select
                value={selectedPeriodId || activePeriod?.id || ""}
                onChange={(e) => setSelectedPeriodId(Number(e.target.value))}
                className="select select-sm select-bordered font-bold"
              >
                {payrollPeriods?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.period_month}/{p.period_year}) - [{p.status.toUpperCase()}]
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => document.getElementById("modal-create-period").showModal()}
                className="btn btn-sm btn-outline gap-1"
              >
                <IconPlus stroke={iconStroke} size={16} />
                New Period
              </button>
            </div>
          </div>

          {/* Active Period Info Card */}
          {activePeriod && (
            <div className="p-4 bg-restro-green/5 border border-restro-border-green rounded-2xl flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-restro-green text-white flex items-center justify-center font-bold">
                  {activePeriod.period_month}
                </div>
                <div>
                  <h4 className="font-black text-restro-text text-lg">{activePeriod.name}</h4>
                  <p className="text-xs text-gray-500 font-mono">
                    Window: {activePeriod.start_date} to {activePeriod.end_date} | Cutoff: {activePeriod.cutoff_date}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase">Batch Gross</p>
                  <p className="text-lg font-black text-restro-text">{formatMoney(activePeriod.total_gross_salary)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase">Batch Net Due</p>
                  <p className="text-lg font-black text-emerald-700">{formatMoney(activePeriod.total_net_salary)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-gray-400 uppercase">Batch Paid</p>
                  <p className="text-lg font-black text-blue-600">{formatMoney(activePeriod.total_paid_salary)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Employee Breakdown Table */}
          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Employee</th>
                  <th>Days (Work / Abs)</th>
                  <th>Base & Allow</th>
                  <th>Gross Salary</th>
                  <th>Deductions (Abs + SSF)</th>
                  <th>Advances & Holds</th>
                  <th>Net Due Now</th>
                  <th>Paid & Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {periodEmployees?.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-10 text-gray-400">
                      No employees calculated in this period yet. Click "Calculate & Freeze" to process attendance snapshot.
                    </td>
                  </tr>
                ) : (
                  periodEmployees?.map((pe) => (
                    <tr key={pe.id} className="hover:bg-gray-50/50">
                      <td>
                        <p className="font-bold text-restro-text">{pe.first_name} {pe.last_name}</p>
                        <p className="text-xs text-gray-400 font-mono">{pe.employee_code}</p>
                      </td>

                      <td className="font-mono text-xs">
                        <span className="text-emerald-700 font-bold">{pe.worked_days}w</span> / <span className="text-rose-500">{pe.absent_days}a</span>
                      </td>

                      <td>
                        <p className="font-mono text-xs">{formatMoney(pe.base_salary)}</p>
                        <p className="text-[10px] text-gray-400">+ {formatMoney(pe.allowances)} allow</p>
                      </td>

                      <td className="font-mono text-xs font-bold text-restro-text">
                        {formatMoney(pe.gross_salary)}
                      </td>

                      <td>
                        <p className="font-mono text-xs text-rose-600">-{formatMoney(Number(pe.absence_deductions || 0) + Number(pe.ssf_employee_amount || 0))}</p>
                        <p className="text-[10px] text-gray-400">SSF 11%: {formatMoney(pe.ssf_employee_amount)}</p>
                      </td>

                      <td>
                        <p className="font-mono text-xs text-purple-600">Rec: {formatMoney(pe.advance_deduction)}</p>
                        {Number(pe.hold_deduction || 0) > 0 && (
                          <p className="text-[10px] text-amber-600">Hold: {formatMoney(pe.hold_deduction)}</p>
                        )}
                      </td>

                      <td className="font-mono text-sm font-black text-emerald-700">
                        {formatMoney(pe.net_payable_amount)}
                      </td>

                      <td>
                        <div className="flex flex-col">
                          <span className={`badge badge-xs uppercase font-bold ${pe.payment_status === "paid" ? "badge-success text-white" : pe.payment_status === "partial" ? "badge-warning text-white" : "badge-ghost"}`}>
                            {pe.payment_status}
                          </span>
                          <span className="text-[10px] text-gray-400 font-mono mt-0.5">Paid: {formatMoney(pe.paid_amount)}</span>
                        </div>
                      </td>

                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handlePrintPayslip(pe.employee_id, pe.period_id)}
                            className="btn btn-ghost btn-xs text-restro-green"
                            title="View Payslip"
                          >
                            <IconPrinter stroke={iconStroke} size={15} />
                          </button>

                          {pe.payment_status !== "paid" && (
                            <button
                              onClick={() => {
                                setPaymentForm({
                                  employee_id: pe.employee_id,
                                  period_id: pe.period_id,
                                  amount: Number(pe.net_payable_amount) - Number(pe.paid_amount || 0),
                                  payment_mode: "bank_transfer",
                                  reference_no: "",
                                  notes: ""
                                });
                                document.getElementById("modal-record-payment").showModal();
                              }}
                              className="btn btn-xs bg-restro-green text-white border-none"
                            >
                              Disburse
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 13: SALARY PAYMENTS & DISBURSEMENTS                 */}
      {/* ======================================================== */}
      {activeTab === "payments" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-base-100 p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <h3 className="font-bold text-restro-text">Disbursement Register</h3>
              <p className="text-xs text-gray-400">All payment installments post balanced double-entry journals: Dr. Salaries Payable, Cr. Cash/Bank</p>
            </div>
            <button
              onClick={() => {
                setPaymentForm({
                  employee_id: "",
                  period_id: currentPeriodId,
                  amount: 0,
                  payment_mode: "bank_transfer",
                  reference_no: "",
                  notes: ""
                });
                document.getElementById("modal-record-payment").showModal();
              }}
              className="btn btn-sm bg-restro-green text-white border-none gap-1"
            >
              <IconPlus stroke={iconStroke} size={16} />
              Record Payment
            </button>
          </div>

          <div className="bg-base-100 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-gray-50/50 text-gray-400 uppercase text-[11px] font-bold">
                  <th>Payment Date</th>
                  <th>Employee</th>
                  <th>Period</th>
                  <th>Amount Paid</th>
                  <th>Payment Mode</th>
                  <th>Reference No</th>
                </tr>
              </thead>
              <tbody>
                {salaryPayments?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-400">
                      No disbursements recorded for this period yet.
                    </td>
                  </tr>
                ) : (
                  salaryPayments?.map((pmt) => (
                    <tr key={pmt.id} className="hover:bg-gray-50/50">
                      <td className="font-mono text-xs">{pmt.payment_date}</td>
                      <td>
                        <p className="font-bold text-restro-text">{pmt.first_name} {pmt.last_name}</p>
                        <p className="text-xs text-gray-400 font-mono">{pmt.employee_code}</p>
                      </td>
                      <td className="font-semibold text-xs text-restro-text">{pmt.period_name}</td>
                      <td className="font-mono text-xs font-bold text-emerald-700">{formatMoney(pmt.amount)}</td>
                      <td>
                        <span className="badge badge-xs uppercase font-bold badge-ghost">{pmt.payment_mode}</span>
                      </td>
                      <td className="font-mono text-xs text-gray-500">{pmt.reference_no || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 14: NEPAL SSF & STATUTORY SETTINGS                   */}
      {/* ======================================================== */}
      {activeTab === "statutory" && (
        <div className="max-w-2xl bg-base-100 p-6 rounded-2xl border border-gray-100 shadow-sm space-y-6">
          <div>
            <h3 className="font-black text-xl text-restro-text flex items-center gap-2">
              <IconSettings stroke={iconStroke} className="text-restro-green" />
              Nepal Social Security Fund (SSF) & Statutory Setup
            </h3>
            <p className="text-xs text-gray-400 mt-1">
              Statutory configuration compliant with the Nepal Social Security Act & Labor Act
            </p>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await updateStatutorySettings(statutorySettings);
                toast.success("Statutory settings updated successfully!");
                await mutateStatutory();
              } catch (err) {
                toast.error(err || "Failed to update statutory settings");
              }
            }}
            className="space-y-4"
          >
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-4">
                <input
                  type="checkbox"
                  checked={statutorySettings?.ssf_enabled === 1}
                  onChange={(e) => mutateStatutory({ ...statutorySettings, ssf_enabled: e.target.checked ? 1 : 0 }, false)}
                  className="checkbox checkbox-success"
                />
                <div>
                  <span className="label-text font-bold text-restro-text">Enable Social Security Fund (SSF) Deductions</span>
                  <p className="text-xs text-gray-400">Automatically calculate and record employee and employer SSF liabilities</p>
                </div>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label label-text text-xs font-bold text-gray-500">Employee Contribution %</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={statutorySettings?.ssf_employee_rate ?? 11.00}
                    onChange={(e) => mutateStatutory({ ...statutorySettings, ssf_employee_rate: parseFloat(e.target.value) }, false)}
                    className="input input-sm input-bordered w-full font-bold"
                  />
                  <span className="absolute right-3 top-2 text-xs text-gray-400 font-bold">% (11%)</span>
                </div>
              </div>

              <div>
                <label className="label label-text text-xs font-bold text-gray-500">Employer Contribution %</label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={statutorySettings?.ssf_employer_rate ?? 20.00}
                    onChange={(e) => mutateStatutory({ ...statutorySettings, ssf_employer_rate: parseFloat(e.target.value) }, false)}
                    className="input input-sm input-bordered w-full font-bold"
                  />
                  <span className="absolute right-3 top-2 text-xs text-gray-400 font-bold">% (20%)</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label label-text text-xs font-bold text-gray-500">Overtime Standard Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  value={statutorySettings?.overtime_rate_multiplier ?? 1.5}
                  onChange={(e) => mutateStatutory({ ...statutorySettings, overtime_rate_multiplier: parseFloat(e.target.value) }, false)}
                  className="input input-sm input-bordered w-full font-bold"
                />
              </div>

              <div>
                <label className="label label-text text-xs font-bold text-gray-500">Holiday Overtime Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  value={statutorySettings?.holiday_overtime_multiplier ?? 2.0}
                  onChange={(e) => mutateStatutory({ ...statutorySettings, holiday_overtime_multiplier: parseFloat(e.target.value) }, false)}
                  className="input input-sm input-bordered w-full font-bold"
                />
              </div>
            </div>

            <div>
              <label className="label label-text text-xs font-bold text-gray-500">Applicable Fiscal Year</label>
              <input
                type="text"
                placeholder="2081/2082 or 2026/2027"
                value={statutorySettings?.fiscal_year ?? "2081/2082"}
                onChange={(e) => mutateStatutory({ ...statutorySettings, fiscal_year: e.target.value }, false)}
                className="input input-sm input-bordered w-full font-bold font-mono"
              />
            </div>

            <button type="submit" className="btn btn-sm bg-restro-green text-white border-none w-full">
              Save Statutory Settings
            </button>
          </form>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: ADD / EDIT EMPLOYEE                               */}
      {/* ======================================================== */}
      <dialog id="modal-add-employee" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-2xl p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-lg text-restro-text">
              {editingEmployee ? "Edit Staff Member" : "Register New Employee"}
            </h3>
            <button onClick={() => document.getElementById("modal-add-employee").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <div className="my-2 p-3 bg-amber-50 rounded-xl text-xs text-amber-800">
            Staff registration does NOT require a login account. Employees can exist for payroll, biometric attendance, and shifts without creating login credentials.
          </div>

          <form onSubmit={handleSaveEmployee} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">First Name *</label>
                <input
                  type="text"
                  value={employeeForm.first_name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, first_name: e.target.value })}
                  className="input input-sm input-bordered w-full"
                  required
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Last Name *</label>
                <input
                  type="text"
                  value={employeeForm.last_name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, last_name: e.target.value })}
                  className="input input-sm input-bordered w-full"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Email</label>
                <input
                  type="email"
                  value={employeeForm.email}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                  className="input input-sm input-bordered w-full"
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Phone</label>
                <input
                  type="text"
                  value={employeeForm.phone}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                  className="input input-sm input-bordered w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Department</label>
                <select
                  value={employeeForm.department_id}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, department_id: e.target.value })}
                  className="select select-sm select-bordered w-full"
                >
                  <option value="">Select Dept</option>
                  {departments?.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-text text-xs font-bold text-gray-500">Designation</label>
                <select
                  value={employeeForm.designation_id}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, designation_id: e.target.value })}
                  className="select select-sm select-bordered w-full"
                >
                  <option value="">Select Title</option>
                  {designations?.map((dg) => (
                    <option key={dg.id} value={dg.id}>{dg.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-text text-xs font-bold text-gray-500">Employment Type</label>
                <select
                  value={employeeForm.employment_type}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, employment_type: e.target.value })}
                  className="select select-sm select-bordered w-full"
                >
                  <option value="permanent">Permanent</option>
                  <option value="probation">Probation</option>
                  <option value="contract">Contract</option>
                  <option value="part_time">Part-Time</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Basic Monthly Salary ({currencySymbol}) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={employeeForm.basic_salary}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, basic_salary: parseFloat(e.target.value) || 0 })}
                  className="input input-sm input-bordered w-full font-bold font-mono"
                  required
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Monthly Allowance ({currencySymbol})</label>
                <input
                  type="number"
                  step="0.01"
                  value={employeeForm.monthly_allowance}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, monthly_allowance: parseFloat(e.target.value) || 0 })}
                  className="input input-sm input-bordered w-full font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Biometric PIN / ADMS ID</label>
                <input
                  type="number"
                  placeholder="e.g. 1001"
                  value={employeeForm.device_user_id}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, device_user_id: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                />
              </div>

              <div>
                <label className="label-text text-xs font-bold text-gray-500">SSF Number</label>
                <input
                  type="text"
                  placeholder="Nepal SSF #"
                  value={employeeForm.ssf_number}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, ssf_number: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                />
              </div>

              <div>
                <label className="label-text text-xs font-bold text-gray-500">PAN Number</label>
                <input
                  type="text"
                  placeholder="PAN"
                  value={employeeForm.pan_no}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, pan_no: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Bank Name</label>
                <input
                  type="text"
                  placeholder="Bank Name"
                  value={employeeForm.bank_name}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, bank_name: e.target.value })}
                  className="input input-sm input-bordered w-full"
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Account Number</label>
                <input
                  type="text"
                  placeholder="Account #"
                  value={employeeForm.bank_account_no}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, bank_account_no: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                />
              </div>
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-employee").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="btn btn-sm bg-restro-green text-white border-none"
              >
                {editingEmployee ? "Update Employee" : "Register Employee"}
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: CREATE LOGIN FOR EMPLOYEE                        */}
      {/* ======================================================== */}
      <dialog id="modal-create-login" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">
              Create Login for {selectedEmployeeForLogin?.first_name}
            </h3>
            <button onClick={() => document.getElementById("modal-create-login").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <p className="text-xs text-gray-400 mt-2">
            Assign credentials for Employee Self-Service. Unlinking later will never delete historical payroll or attendance.
          </p>

          <form onSubmit={handleCreateLogin} className="space-y-3 mt-4">
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Username *</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="input input-sm input-bordered w-full font-mono"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Password *</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="input input-sm input-bordered w-full"
                required
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-create-login").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="btn btn-sm bg-restro-green text-white border-none"
              >
                Link Account
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: PRINTABLE BRANDED PAYSLIP                        */}
      {/* ======================================================== */}
      <dialog id="modal-view-payslip" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-2xl p-8 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-black text-restro-text tracking-tight uppercase">SALARY PAYSLIP</h2>
              <p className="text-xs text-gray-400 font-mono">Period: {payslipData?.period_name || "Monthly"}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => window.print()} className="btn btn-sm btn-outline gap-1">
                <IconPrinter stroke={iconStroke} size={16} />
                Print
              </button>
              <button onClick={() => document.getElementById("modal-view-payslip").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
            </div>
          </div>

          {payslipData ? (
            <div className="space-y-6 mt-4">
              {/* Employee Meta Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-xl text-xs">
                <div>
                  <span className="text-gray-400 font-bold uppercase">Employee</span>
                  <p className="font-black text-restro-text mt-0.5">{payslipData.first_name} {payslipData.last_name}</p>
                  <p className="font-mono text-gray-400">{payslipData.employee_code}</p>
                </div>
                <div>
                  <span className="text-gray-400 font-bold uppercase">Department</span>
                  <p className="font-semibold text-restro-text mt-0.5">{payslipData.department_name}</p>
                  <p className="text-gray-400">{payslipData.designation_name}</p>
                </div>
                <div>
                  <span className="text-gray-400 font-bold uppercase">Bank & PAN</span>
                  <p className="font-mono font-semibold text-restro-text mt-0.5">{payslipData.bank_account_no || "Cash"}</p>
                  <p className="font-mono text-gray-400">PAN: {payslipData.pan_no || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-400 font-bold uppercase">Days Worked</span>
                  <p className="font-bold text-restro-text mt-0.5">{payslipData.worked_days} Days</p>
                  <p className="text-rose-500 font-mono">Abs: {payslipData.absent_days}d</p>
                </div>
              </div>

              {/* Earnings vs Deductions Table */}
              <div className="grid grid-cols-2 gap-4">
                {/* Earnings */}
                <div className="border border-gray-100 rounded-xl p-4">
                  <h4 className="font-bold text-xs uppercase text-gray-400 pb-2 border-b border-gray-100">Earnings</h4>
                  <div className="divide-y divide-gray-50 text-xs mt-2">
                    <div className="py-1.5 flex justify-between">
                      <span>Basic Salary</span>
                      <span className="font-mono font-bold">{formatMoney(payslipData.base_salary)}</span>
                    </div>
                    <div className="py-1.5 flex justify-between">
                      <span>Monthly Allowance</span>
                      <span className="font-mono font-bold">{formatMoney(payslipData.allowances)}</span>
                    </div>
                    <div className="py-1.5 flex justify-between">
                      <span>Overtime Pay</span>
                      <span className="font-mono font-bold">{formatMoney(payslipData.overtime_pay)}</span>
                    </div>
                    <div className="py-2 flex justify-between font-black text-sm border-t border-gray-200">
                      <span>Gross Earnings</span>
                      <span className="font-mono text-restro-text">{formatMoney(payslipData.gross_salary)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="border border-gray-100 rounded-xl p-4">
                  <h4 className="font-bold text-xs uppercase text-gray-400 pb-2 border-b border-gray-100">Deductions</h4>
                  <div className="divide-y divide-gray-50 text-xs mt-2">
                    <div className="py-1.5 flex justify-between">
                      <span>Absence Deduction</span>
                      <span className="font-mono text-rose-600">-{formatMoney(payslipData.absence_deductions)}</span>
                    </div>
                    <div className="py-1.5 flex justify-between">
                      <span>Employee SSF (11%)</span>
                      <span className="font-mono text-rose-600">-{formatMoney(payslipData.ssf_employee_amount)}</span>
                    </div>
                    <div className="py-1.5 flex justify-between">
                      <span>Advance Recovery</span>
                      <span className="font-mono text-rose-600">-{formatMoney(payslipData.advance_deduction)}</span>
                    </div>
                    {Number(payslipData.hold_deduction || 0) > 0 && (
                      <div className="py-1.5 flex justify-between">
                        <span>Salary Hold</span>
                        <span className="font-mono text-amber-600">-{formatMoney(payslipData.hold_deduction)}</span>
                      </div>
                    )}
                    <div className="py-2 flex justify-between font-black text-sm border-t border-gray-200">
                      <span>Total Deductions</span>
                      <span className="font-mono text-rose-600">-{formatMoney(Number(payslipData.absence_deductions || 0) + Number(payslipData.ssf_employee_amount || 0) + Number(payslipData.advance_deduction || 0) + Number(payslipData.hold_deduction || 0))}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Take-Home Highlight */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-emerald-800 uppercase">Net Take-Home Pay</span>
                  <p className="text-xs text-emerald-600">Disbursed to Bank Account / Cash</p>
                </div>
                <h3 className="text-2xl font-black text-emerald-800 font-mono">
                  {formatMoney(payslipData.net_payable_amount)}
                </h3>
              </div>

              {/* Employer Contributions Note */}
              <div className="p-3 bg-indigo-50/50 rounded-xl text-xs text-indigo-900 flex justify-between">
                <span>Employer Social Security Contribution (20% SSF):</span>
                <span className="font-mono font-bold">{formatMoney(payslipData.ssf_employer_amount)}</span>
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-2 gap-12 pt-8 text-center text-xs text-gray-400">
                <div className="border-t border-gray-200 pt-2">
                  Employee Signature
                </div>
                <div className="border-t border-gray-200 pt-2">
                  Authorized HR Signatory
                </div>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-gray-400">Loading payslip details...</div>
          )}
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: CREATE PAYROLL PERIOD                             */}
      {/* ======================================================== */}
      <dialog id="modal-create-period" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Create Payroll Period</h3>
            <button onClick={() => document.getElementById("modal-create-period").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createPayrollPeriod(periodForm);
                toast.success("Payroll period created!");
                await mutatePeriods();
                document.getElementById("modal-create-period").close();
              } catch (err) {
                toast.error(err || "Failed to create payroll period");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Period Name *</label>
              <input
                type="text"
                value={periodForm.name}
                onChange={(e) => setPeriodForm({ ...periodForm, name: e.target.value })}
                className="input input-sm input-bordered w-full font-bold"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Month</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={periodForm.period_month}
                  onChange={(e) => setPeriodForm({ ...periodForm, period_month: parseInt(e.target.value) })}
                  className="input input-sm input-bordered w-full"
                  required
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Year</label>
                <input
                  type="number"
                  value={periodForm.period_year}
                  onChange={(e) => setPeriodForm({ ...periodForm, period_year: parseInt(e.target.value) })}
                  className="input input-sm input-bordered w-full"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Start Date</label>
              <input
                type="date"
                value={periodForm.start_date}
                onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })}
                className="input input-sm input-bordered w-full"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">End Date</label>
              <input
                type="date"
                value={periodForm.end_date}
                onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })}
                className="input input-sm input-bordered w-full"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Cutoff Date (Attendance Freeze)</label>
              <input
                type="date"
                value={periodForm.cutoff_date}
                onChange={(e) => setPeriodForm({ ...periodForm, cutoff_date: e.target.value })}
                className="input input-sm input-bordered w-full font-bold text-restro-green"
                required
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-create-period").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Create Period
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: RECORD SALARY PAYMENT                             */}
      {/* ======================================================== */}
      <dialog id="modal-record-payment" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Record Salary Disbursement</h3>
            <button onClick={() => document.getElementById("modal-record-payment").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form onSubmit={handleRecordPayment} className="space-y-3 mt-4">
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Employee *</label>
              <select
                value={paymentForm.employee_id}
                onChange={(e) => setPaymentForm({ ...paymentForm, employee_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Amount ({currencySymbol}) *</label>
              <input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                className="input input-sm input-bordered w-full font-bold font-mono"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Payment Mode</label>
              <select
                value={paymentForm.payment_mode}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })}
                className="select select-sm select-bordered w-full"
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Reference / Cheque #</label>
              <input
                type="text"
                placeholder="TRX-12345"
                value={paymentForm.reference_no}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference_no: e.target.value })}
                className="input input-sm input-bordered w-full font-mono"
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-record-payment").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" disabled={isProcessing} className="btn btn-sm bg-restro-green text-white border-none">
                Disburse & Post
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: ADD HOLIDAY                                       */}
      {/* ======================================================== */}
      <dialog id="modal-add-holiday" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Add Holiday</h3>
            <button onClick={() => document.getElementById("modal-add-holiday").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await addHoliday(holidayForm);
                toast.success("Holiday added!");
                await mutateHolidays();
                document.getElementById("modal-add-holiday").close();
              } catch (err) {
                toast.error(err || "Failed to add holiday");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Holiday Title *</label>
              <input
                type="text"
                placeholder="e.g. Dashain / New Year"
                value={holidayForm.title}
                onChange={(e) => setHolidayForm({ ...holidayForm, title: e.target.value })}
                className="input input-sm input-bordered w-full font-bold"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Date *</label>
              <input
                type="date"
                value={holidayForm.holiday_date}
                onChange={(e) => setHolidayForm({ ...holidayForm, holiday_date: e.target.value })}
                className="input input-sm input-bordered w-full font-bold"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Type</label>
              <select
                value={holidayForm.holiday_type}
                onChange={(e) => setHolidayForm({ ...holidayForm, holiday_type: e.target.value })}
                className="select select-sm select-bordered w-full"
              >
                <option value="public">Public Holiday</option>
                <option value="festival">Festival Holiday</option>
                <option value="tenant">Company Holiday</option>
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Overtime Multiplier</label>
              <input
                type="number"
                step="0.1"
                value={holidayForm.overtime_multiplier}
                onChange={(e) => setHolidayForm({ ...holidayForm, overtime_multiplier: parseFloat(e.target.value) })}
                className="input input-sm input-bordered w-full font-bold"
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-holiday").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Add Holiday
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: ADD SHIFT (UPGRADED)                              */}
      {/* ======================================================== */}
      <dialog id="modal-add-shift-upgraded" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Create Shift</h3>
            <button onClick={() => document.getElementById("modal-add-shift-upgraded").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await addNewShift(shiftForm.name, shiftForm.start_time, shiftForm.end_time);
                toast.success("Shift created!");
                await mutateShifts();
                document.getElementById("modal-add-shift-upgraded").close();
              } catch (err) {
                toast.error(err || "Failed to create shift");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Shift Name *</label>
              <input
                type="text"
                placeholder="Morning / Night Shift"
                value={shiftForm.name}
                onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
                className="input input-sm input-bordered w-full font-bold"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Start Time</label>
                <input
                  type="time"
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                  required
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">End Time</label>
                <input
                  type="time"
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                  required
                />
              </div>
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-shift-upgraded").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Save Shift
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: MANUAL ATTENDANCE CORRECTION                      */}
      {/* ======================================================== */}
      <dialog id="modal-manual-attendance" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Manual Attendance Correction</h3>
            <button onClick={() => document.getElementById("modal-manual-attendance").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!manualAttForm.employee_id) return;
              try {
                await manualAttendanceEntry(manualAttForm);
                toast.success("Attendance corrected successfully!");
                await mutateAttendance();
                document.getElementById("modal-manual-attendance").close();
              } catch (err) {
                toast.error(err || "Failed to record manual attendance");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Employee *</label>
              <select
                value={manualAttForm.employee_id}
                onChange={(e) => setManualAttForm({ ...manualAttForm, employee_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Date *</label>
              <input
                type="date"
                value={manualAttForm.attendance_date}
                onChange={(e) => setManualAttForm({ ...manualAttForm, attendance_date: e.target.value })}
                className="input input-sm input-bordered w-full"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Check In</label>
                <input
                  type="time"
                  value={manualAttForm.check_in}
                  onChange={(e) => setManualAttForm({ ...manualAttForm, check_in: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Check Out</label>
                <input
                  type="time"
                  value={manualAttForm.check_out}
                  onChange={(e) => setManualAttForm({ ...manualAttForm, check_out: e.target.value })}
                  className="input input-sm input-bordered w-full font-mono"
                />
              </div>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Status</label>
              <select
                value={manualAttForm.status}
                onChange={(e) => setManualAttForm({ ...manualAttForm, status: e.target.value })}
                className="select select-sm select-bordered w-full"
              >
                <option value="present">Present</option>
                <option value="half_day">Half Day</option>
                <option value="absent">Absent</option>
                <option value="on_leave">On Leave</option>
                <option value="holiday">Holiday</option>
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Correction Reason (Audit Trail) *</label>
              <input
                type="text"
                placeholder="Reason for manual adjustment"
                value={manualAttForm.remarks}
                onChange={(e) => setManualAttForm({ ...manualAttForm, remarks: e.target.value })}
                className="input input-sm input-bordered w-full"
                required
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-manual-attendance").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Save Correction
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: APPLY LEAVE                                       */}
      {/* ======================================================== */}
      <dialog id="modal-apply-leave" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Apply Leave</h3>
            <button onClick={() => document.getElementById("modal-apply-leave").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await applyLeave(leaveForm);
                toast.success("Leave applied successfully!");
                await mutateLeaveApps();
                document.getElementById("modal-apply-leave").close();
              } catch (err) {
                toast.error(err || "Failed to apply leave");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Employee *</label>
              <select
                value={leaveForm.employee_id}
                onChange={(e) => setLeaveForm({ ...leaveForm, employee_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Leave Type *</label>
              <select
                value={leaveForm.leave_type_id}
                onChange={(e) => setLeaveForm({ ...leaveForm, leave_type_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Leave Type</option>
                {leaveTypes?.map((lt) => (
                  <option key={lt.id} value={lt.id}>{lt.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Start Date</label>
                <input
                  type="date"
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                  className="input input-sm input-bordered w-full"
                  required
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">End Date</label>
                <input
                  type="date"
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                  className="input input-sm input-bordered w-full"
                  required
                />
              </div>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Total Days</label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={leaveForm.total_days}
                onChange={(e) => setLeaveForm({ ...leaveForm, total_days: parseFloat(e.target.value) })}
                className="input input-sm input-bordered w-full font-bold"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Reason</label>
              <input
                type="text"
                placeholder="Reason for leave"
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                className="input input-sm input-bordered w-full"
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-apply-leave").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Submit Application
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: RECORD OVERTIME                                   */}
      {/* ======================================================== */}
      <dialog id="modal-add-overtime" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Record Overtime</h3>
            <button onClick={() => document.getElementById("modal-add-overtime").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createOvertimeRecord(overtimeForm);
                toast.success("Overtime record submitted!");
                await mutateOvertime();
                document.getElementById("modal-add-overtime").close();
              } catch (err) {
                toast.error(err || "Failed to record overtime");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Employee *</label>
              <select
                value={overtimeForm.employee_id}
                onChange={(e) => setOvertimeForm({ ...overtimeForm, employee_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Date</label>
              <input
                type="date"
                value={overtimeForm.overtime_date}
                onChange={(e) => setOvertimeForm({ ...overtimeForm, overtime_date: e.target.value })}
                className="input input-sm input-bordered w-full"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Hours</label>
                <input
                  type="number"
                  step="0.5"
                  value={overtimeForm.calculated_hours}
                  onChange={(e) => setOvertimeForm({ ...overtimeForm, calculated_hours: parseFloat(e.target.value), approved_hours: parseFloat(e.target.value) })}
                  className="input input-sm input-bordered w-full font-bold"
                  required
                />
              </div>
              <div>
                <label className="label-text text-xs font-bold text-gray-500">Multiplier</label>
                <input
                  type="number"
                  step="0.1"
                  value={overtimeForm.hourly_rate_multiplier}
                  onChange={(e) => setOvertimeForm({ ...overtimeForm, hourly_rate_multiplier: parseFloat(e.target.value) })}
                  className="input input-sm input-bordered w-full font-bold"
                  required
                />
              </div>
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-overtime").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Submit Overtime
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: ISSUE SALARY ADVANCE                              */}
      {/* ======================================================== */}
      <dialog id="modal-add-advance-upgraded" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Issue Salary Advance</h3>
            <button onClick={() => document.getElementById("modal-add-advance-upgraded").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createSalaryAdvance(advanceForm);
                toast.success("Salary advance request registered!");
                await mutateAdvances();
                document.getElementById("modal-add-advance-upgraded").close();
              } catch (err) {
                toast.error(err || "Failed to create advance request");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Employee *</label>
              <select
                value={advanceForm.employee_id}
                onChange={(e) => setAdvanceForm({ ...advanceForm, employee_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Advance Amount ({currencySymbol}) *</label>
              <input
                type="number"
                step="0.01"
                value={advanceForm.amount}
                onChange={(e) => setAdvanceForm({ ...advanceForm, amount: parseFloat(e.target.value) || 0 })}
                className="input input-sm input-bordered w-full font-bold font-mono"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Monthly Installment Deduction ({currencySymbol})</label>
              <input
                type="number"
                step="0.01"
                value={advanceForm.monthly_installment}
                onChange={(e) => setAdvanceForm({ ...advanceForm, monthly_installment: parseFloat(e.target.value) || 0 })}
                className="input input-sm input-bordered w-full font-mono"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Reason</label>
              <input
                type="text"
                placeholder="Personal emergency / medical"
                value={advanceForm.reason}
                onChange={(e) => setAdvanceForm({ ...advanceForm, reason: e.target.value })}
                className="input input-sm input-bordered w-full"
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-add-advance-upgraded").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Submit Advance
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: RECORD SECURITY DEPOSIT RECEIPT                   */}
      {/* ======================================================== */}
      <dialog id="modal-record-deposit" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Receive Security Deposit</h3>
            <button onClick={() => document.getElementById("modal-record-deposit").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await recordSecurityDepositReceipt(depositForm);
                toast.success("Security deposit recorded as liability!");
                await mutateDeposits();
                document.getElementById("modal-record-deposit").close();
              } catch (err) {
                toast.error(err || "Failed to record security deposit");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Employee *</label>
              <select
                value={depositForm.employee_id}
                onChange={(e) => setDepositForm({ ...depositForm, employee_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Deposit Amount ({currencySymbol}) *</label>
              <input
                type="number"
                step="0.01"
                value={depositForm.amount}
                onChange={(e) => setDepositForm({ ...depositForm, amount: parseFloat(e.target.value) || 0 })}
                className="input input-sm input-bordered w-full font-bold font-mono"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Payment Mode</label>
              <select
                value={depositForm.payment_mode}
                onChange={(e) => setDepositForm({ ...depositForm, payment_mode: e.target.value })}
                className="select select-sm select-bordered w-full"
              >
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Reference #</label>
              <input
                type="text"
                placeholder="TRX-DEP-001"
                value={depositForm.reference_no}
                onChange={(e) => setDepositForm({ ...depositForm, reference_no: e.target.value })}
                className="input input-sm input-bordered w-full font-mono"
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-record-deposit").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Record Liability
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* ======================================================== */}
      {/* MODAL: RECORD SALARY HOLD                                */}
      {/* ======================================================== */}
      <dialog id="modal-record-hold" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box max-w-sm p-6 rounded-2xl bg-base-100">
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h3 className="font-bold text-base text-restro-text">Record Salary Hold</h3>
            <button onClick={() => document.getElementById("modal-record-hold").close()} className="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createSalaryHold({ ...holdForm, period_id: currentPeriodId });
                toast.success("Salary hold recorded!");
                await mutateHolds();
                document.getElementById("modal-record-hold").close();
              } catch (err) {
                toast.error(err || "Failed to record salary hold");
              }
            }}
            className="space-y-3 mt-4"
          >
            <div>
              <label className="label-text text-xs font-bold text-gray-500">Employee *</label>
              <select
                value={holdForm.employee_id}
                onChange={(e) => setHoldForm({ ...holdForm, employee_id: e.target.value })}
                className="select select-sm select-bordered w-full"
                required
              >
                <option value="">Select Employee</option>
                {employees?.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Hold Amount ({currencySymbol}) *</label>
              <input
                type="number"
                step="0.01"
                value={holdForm.hold_amount}
                onChange={(e) => setHoldForm({ ...holdForm, hold_amount: parseFloat(e.target.value) || 0 })}
                className="input input-sm input-bordered w-full font-bold font-mono"
                required
              />
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Hold Reason</label>
              <select
                value={holdForm.hold_reason}
                onChange={(e) => setHoldForm({ ...holdForm, hold_reason: e.target.value })}
                className="select select-sm select-bordered w-full"
              >
                <option value="notice_period">Notice Period</option>
                <option value="dispute">Disciplinary Dispute</option>
                <option value="pending_clearance">Pending Asset Clearance</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="label-text text-xs font-bold text-gray-500">Remarks</label>
              <input
                type="text"
                placeholder="Justification"
                value={holdForm.remarks}
                onChange={(e) => setHoldForm({ ...holdForm, remarks: e.target.value })}
                className="input input-sm input-bordered w-full"
              />
            </div>

            <div className="modal-action border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => document.getElementById("modal-record-hold").close()}
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-sm bg-restro-green text-white border-none">
                Record Hold
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </Page>
  );
}
